import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Select, { createFilter } from "react-select";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Icon,
  PageHeader,
  Radio,
  SegmentedControl,
  Select as AdminSelect,
  Table,
  TextField,
  Toggle,
  styles as ui,
  useConfirm,
} from "stories/Admin";
import type { Column } from "stories/Admin";
import useFetch from "hooks/useFetch";
import { adminSelectStyles } from "../adminSelectStyles";
import {
  generateStyledExcel,
  PrayerRow,
  AsrFilterOptions,
} from "./excelExport";
import { generatePDF } from "./pdfExport";
import PlacesAutocomplete from "./PlacesAutocomplete";
import CoordinateMap from "./CoordinateMap";
import styles from "./MawaqeetGenerateTimes.module.scss";

// iBønn-API base - bruker useFetch som går gjennom backend
// Backend må ha en proxy-endpoint for prayertimescalculator API

// --- Typer ---

export type LocationInfo = {
  id: number;
  name: string;
};

type PrayerApiTime = {
  h: string;
  m: string;
  s: string;
};

type PrayerApiGregorian = {
  d: number;
  m: number;
  y: number;
};

type PrayerApiOther = {
  gregorian: PrayerApiGregorian;
};

type PrayerApiTimes = {
  /** Bare IRN: asr slik bonnetid.no viser den (1x i sommertid, 2x i vintertid). */
  asr?: PrayerApiTime;
  fajr: PrayerApiTime;
  sunrise: PrayerApiTime;
  dhuhr: PrayerApiTime;
  asr1: PrayerApiTime;
  asr2: PrayerApiTime;
  maghrib: PrayerApiTime;
  isha: PrayerApiTime;
};

type PrayerApiDay = {
  prayer: {
    other: PrayerApiOther;
    times: PrayerApiTimes;
  };
};

type PrayerApiResponse = {
  content: PrayerApiDay[];
};

type DateApiDay = {
  gregorianDate: string; // "YYYY-MM-DD"
  hijriMonth: string;
  hijriDate: string; // "1447-7-12"
  specialDateName: string | null;
};

type HijriMap = {
  [gregDate: string]: string; // "YYYY-MM-DD" -> "1447-7-12"
};

interface PrayerTable {
  id: number;
  name: string;
}

/** Svar fra «Lagre i iBønn» (POST /api/v1/admin/calendar/generated-prayers). */
type SaveResult = {
  saved: number;
  updated: number;
  created: number;
  removedDuplicates: number;
  message: string;
};

interface Location {
  id: number;
  name: string;
  lat: number;
  lon: number;
  prayerTimesTables: {
    id: number;
    name: string;
  }[];
}

// --- helpers ---

function fmtTime(t: PrayerApiTime): string {
  const h = t.h.toString().padStart(2, "0");
  const m = t.m.toString().padStart(2, "0");
  return `${h}:${m}`;
}

function convertHijriFromApi(hijriRaw: string): string {
  // "1447-7-12" -> "12-7-1447"
  const [hyStr, hmStr, hdStr] = hijriRaw.split("-");
  const hy = parseInt(hyStr, 10);
  const hm = parseInt(hmStr, 10);
  const hd = parseInt(hdStr, 10);
  return `${hd}-${hm}-${hy}`;
}

function formatDateDDMMYYYY(y: number, m: number, d: number): string {
  const dd = d.toString().padStart(2, "0");
  const mm = m.toString().padStart(2, "0");
  return `${dd}-${mm}-${y}`;
}

/** Parser «lat,lon» (f.eks. 54.234324,10.3242343). */
function parseLatLonPair(value: string): { lat: string; lon: string } | null {
  const match = value
    .trim()
    .match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
  if (!match) return null;
  const lat = Number(match[1]);
  const lon = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return { lat: match[1], lon: match[2] };
}

function getLastSunday(year: number, month: number): Date {
  const lastDay = new Date(year, month, 0); // last day of month
  let d = lastDay;
  while (d.getDay() !== 0) {
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
  }
  return d;
}

// Sommertid gjelder fra og med siste søndag i mars (byttet skjer kl. 02) og til siste søndag i oktober
// (byttet skjer kl. 03). Asr er på ettermiddagen, så selve byttedagen hører til den nye tiden.
function isSummerTime(date: Date, year: number): boolean {
  const lastSundayMarch = getLastSunday(year, 3);
  const lastSundayOctober = getLastSunday(year, 10);
  return date >= lastSundayMarch && date < lastSundayOctober;
}

const MONTH_NAMES_NO: Record<number, string> = {
  1: "Januar",
  2: "Februar",
  3: "Mars",
  4: "April",
  5: "Mai",
  6: "Juni",
  7: "Juli",
  8: "August",
  9: "September",
  10: "Oktober",
  11: "November",
  12: "Desember",
};

const MONTH_SHORT_NO = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Des",
];

/** Metode: IRN («Standard», bonnetid.no) eller Mawaqeet (iBønns egen eller gammel tredjeparts-URL). */
export type GenerateMethod = "irn" | "mawaqeet" | "mawaqeet-external";

const METHOD_INFO: Record<
  GenerateMethod,
  { label: string; title: string; text: React.ReactNode }
> = {
  irn: {
    label: "IRN (Standard, bonnetid.no)",
    title: "IRN – Islamsk Råd Norge",
    text: (
      <>
        Samme tider som bonnetid.no og tabellen «Standard» i iBønn. IRN har ikke
        publisert metoden, så iBønn har funnet reglene ved å sammenligne med
        tidene deres: over 99 % likt på minuttet i Oslo, Bergen og Kristiansand,
        ellers 1 minutt unna.
        <ul>
          <li>
            Fajr: sola 16° under horisonten. Om sommeren står fajr fast når
            natten blir for lys, men aldri senere enn 58 min før soloppgang.
          </li>
          <li>Soloppgang: 2 min før selve soloppgangen.</li>
          <li>
            Dhuhr: 4 min etter at sola står i sør (9 min fra 21. mars til 20.
            september).
          </li>
          <li>
            Asr: skygge 1× og 2×. «Asr» er 1× i sommertid og 2× i vintertid.
          </li>
          <li>
            Maghrib: 4 min etter solnedgang. Isha: sola 15° under horisonten; om
            sommeren glir isha mot solnedgang + 45 min rundt 21. juni, aldri før
            maghrib + 40.
          </li>
        </ul>
        Nord-Norge med midnattssol og mørketid (f.eks. Tromsø og Alta) støttes
        ikke ennå. De byene hoppes over og må fortsatt hentes fra bonnetid.no.
      </>
    ),
  },
  mawaqeet: {
    label: "Mawaqeet (iBønns egen beregning)",
    title: "Mawaqeet",
    text: (
      <>
        Mawaqeet-metoden, regnet ut av iBønn.
        <ul>
          <li>
            Fajr: sola 18° under horisonten. I ekstremperioden brukes midten av
            natten (nisf ul-layl).
          </li>
          <li>
            Dhuhr: 5 min etter at sola står i sør. Asr: skygge 1× og 2× pluss
            middagsskyggen.
          </li>
          <li>
            Maghrib: 4 min etter solnedgang. Isha: sola 15° under horisonten; i
            ekstremperioden bes isha etter maghrib.
          </li>
        </ul>
        Virker over hele Norge, også nord for polarsirkelen.
      </>
    ),
  },
  "mawaqeet-external": {
    label: "Mawaqeet (gammel URL, tredjepart)",
    title: "Mawaqeet fra tredjepart",
    text: (
      <>
        Den gamle Mawaqeet-kalkulatoren hos en tredjepart. Samme metode som
        over, men gir feil nord for polarsirkelen. Brukes bare for å
        sammenligne.
      </>
    ),
  },
};

// --- komponent ---

interface MawaqeetGenerateTimesProps {
  /** Superadmin «Bønnetider › Generer tider»: IRN kan velges, og Hijri-kalenderen fylles ut ved behov. */
  superAdmin?: boolean;
}

const MawaqeetGenerateTimes: React.FC<MawaqeetGenerateTimesProps> = ({
  superAdmin = false,
}) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null,
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear() + 1,
  );
  const [rows, setRows] = useState<PrayerRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Asr-filter valg
  const [onlyShafi, setOnlyShafi] = useState(false);

  // Metode: IRN (bare superadmin), Mawaqeet fra iBønn (standard) eller den gamle tredjeparts-URL-en
  const [method, setMethodState] = useState<GenerateMethod>(() => {
    try {
      if (superAdmin && localStorage.getItem("generate-method") === "irn")
        return "irn";
      return localStorage.getItem("mawaqeet-generator") === "external"
        ? "mawaqeet-external"
        : "mawaqeet";
    } catch {
      return "mawaqeet";
    }
  });
  const setMethod = (m: GenerateMethod) => {
    setMethodState(m);
    setRows([]);
    setStatus("");
    setError(null);
    try {
      localStorage.setItem("generate-method", m);
      if (m !== "irn")
        localStorage.setItem(
          "mawaqeet-generator",
          m === "mawaqeet-external" ? "external" : "ibonn",
        );
    } catch {
      /* ignore */
    }
  };
  const isIrn = method === "irn";
  const [onlyHanafi, setOnlyHanafi] = useState(false);

  // Egendefinert lokasjon
  const [useCustomLocation, setUseCustomLocation] = useState(false);
  const [customLat, setCustomLat] = useState<string>("");
  const [customLon, setCustomLon] = useState<string>("");
  const [customCityName, setCustomCityName] = useState<string>("");
  const [coordsCopied, setCoordsCopied] = useState(false);

  // Måned i forhåndsvisningen (kun visning)
  const [previewMonth, setPreviewMonth] = useState(1);

  const { execute: fetchLocations } = useFetch<Location[]>();
  const { execute: fetchPrayerTimes, lastError: lastPrayerError } =
    useFetch<PrayerApiResponse>();
  const { execute: fetchDates } = useFetch<DateApiDay[]>();
  const { execute: fetchTables } = useFetch<PrayerTable[]>();
  const saveApi = useFetch<SaveResult>();
  const confirm = useConfirm();

  // «Lagre i iBønn»: tabellen tidene lagres i, og fremdrift for «alle byer».
  const [tables, setTables] = useState<PrayerTable[]>([]);
  const [targetTableId, setTargetTableId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const stopAll = useRef(false);
  /** Hijri-datoer per måned for valgt år (hentes én gang, også når alle byer genereres). */
  const hijriCache = useRef<{ year: number; months: Record<number, HijriMap> }>(
    { year: 0, months: {} },
  );

  useEffect(() => {
    fetchTables("/api/v1/prayer-times-tables", "GET").then((result) => {
      setTables((result as PrayerTable[] | null) || []);
    });
    // fetchTables lages på nytt av useFetch; tabellene hentes bare én gang.
  }, []);

  // Tabellen følger metoden: IRN → «Standard» (bonnetid), Mawaqeet → Mawaqeet (tabell 2 i CSV-eksporten).
  useEffect(() => {
    if (!tables.length) return;
    const preferred = isIrn
      ? (tables.find((t) => /^standard$/i.test(t.name.trim())) ??
        tables.find((t) => /b(ø|o)nnetid/i.test(t.name)) ??
        tables.find((t) => t.id === 1))
      : (tables.find((t) => /mawaqeet/i.test(t.name)) ??
        tables.find((t) => t.id === 2));
    setTargetTableId((preferred ?? tables[0]).id);
  }, [tables, isIrn]);

  // Fetch locations for dropdown - hent alle byer
  useEffect(() => {
    const fetchLocationData = async () => {
      const result = await fetchLocations("/api/v1/locations", "GET");
      if (result) {
        const allLocations = (result as Location[]).sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        setLocations(allLocations);
        if (allLocations.length > 0) {
          setSelectedLocationId(allLocations[0].id);
        }
      }
    };
    fetchLocationData();
  }, []);

  const selectedLocation =
    locations.find((l) => l.id === selectedLocationId) || null;

  // Callback for Google Places - stabil referanse
  const handlePlaceSelected = useCallback(
    (place: { name: string; lat: number; lon: number }) => {
      setCustomCityName(place.name);
      if (Number.isFinite(place.lat) && Number.isFinite(place.lon)) {
        setCustomLat(place.lat.toString());
        setCustomLon(place.lon.toString());
      }
    },
    [],
  );

  const handleLatChange = (raw: string) => {
    const pair = parseLatLonPair(raw);
    if (pair) {
      setCustomLat(pair.lat);
      setCustomLon(pair.lon);
      return;
    }
    setCustomLat(raw);
  };

  const handleMapCoordinateChange = useCallback((lat: number, lon: number) => {
    setCustomLat(lat.toFixed(6));
    setCustomLon(lon.toFixed(6));
  }, []);

  const handleCopyLatLon = async () => {
    const lat = customLat.trim();
    const lon = customLon.trim();
    if (!lat || !lon) return;
    const text = `${lat},${lon}`;
    try {
      await navigator.clipboard.writeText(text);
      setCoordsCopied(true);
      window.setTimeout(() => setCoordsCopied(false), 1600);
    } catch {
      // Fallback for eldre nettlesere / uten clipboard-permission
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCoordsCopied(true);
      window.setTimeout(() => setCoordsCopied(false), 1600);
    }
  };

  const parsedCustomLat = useMemo(() => {
    const n = Number(customLat.trim());
    return Number.isFinite(n) ? n : null;
  }, [customLat]);

  const parsedCustomLon = useMemo(() => {
    const n = Number(customLon.trim());
    return Number.isFinite(n) ? n : null;
  }, [customLon]);

  // Valider om vi kan generere
  const canGenerate = useCustomLocation
    ? customLat.trim() !== "" &&
      customLon.trim() !== "" &&
      customCityName.trim() !== ""
    : selectedLocation !== null;

  // Hent bynavn for generering
  const getCityName = (): string => {
    if (useCustomLocation) {
      return customCityName.trim();
    }
    return selectedLocation?.name || "Ukjent";
  };

  // Hent location ID for CSV/generering (bruker 0 for egendefinert)
  const getLocationId = (): number => {
    if (useCustomLocation) {
      return 0;
    }
    return selectedLocationId || 0;
  };

  const { execute: generateHijri, lastError: hijriError } = useFetch<{
    created: number;
    missingDates: string[];
  }>();
  /** Året Hijri-kalenderen allerede er forsøkt fylt ut for (superadmin), så det bare skjer én gang. */
  const hijriGenerated = useRef<number | null>(null);

  /** Hijri-datoene for en måned i valgt år (hentes én gang per år og måned). */
  const hijriFor = async (month: number): Promise<HijriMap> => {
    if (hijriCache.current.year !== selectedYear)
      hijriCache.current = { year: selectedYear, months: {} };
    const cached = hijriCache.current.months[month];
    if (cached) return cached;
    const datesJson = await fetchDates(
      `/api/v1/dates/${selectedYear}/${month}`,
      "GET",
    );
    if (!datesJson) {
      throw new Error(`Kunne ikke hente hijri-datoer for måned ${month}.`);
    }
    const hijriMap: HijriMap = {};
    for (const d of datesJson) {
      if (d.hijriDate) hijriMap[d.gregorianDate] = d.hijriDate;
    }
    hijriCache.current.months[month] = hijriMap;
    return hijriMap;
  };

  /**
   * Superadmin: mangler Hijri-datoer for året, fylles de ut fra månedstartene i Hijri-kalenderen
   * (samme som «Generer» under Hijri-datoer), og så hentes måneden på nytt.
   */
  const fillHijriYear = async (): Promise<boolean> => {
    if (!superAdmin || hijriGenerated.current === selectedYear) return false;
    hijriGenerated.current = selectedYear;
    setStatus(`Fyller ut Hijri-kalenderen for ${selectedYear} …`);
    const result = await generateHijri(
      `/api/v1/admin/calendar/${selectedYear}/generate`,
      "POST",
    );
    if (!result)
      throw new Error(
        `Hijri-kalenderen for ${selectedYear} mangler datoer og kunne ikke fylles ut: ${
          hijriError()?.message || "ukjent feil"
        }. Legg inn månedstartene under Hijri-datoer.`,
      );
    hijriCache.current = { year: selectedYear, months: {} };
    return true;
  };

  /** Beregner et helt år for et punkt (12 kall til kalkulatoren), sortert på dato. */
  const buildYear = async (
    lat: number,
    lon: number,
    cityName: string,
    locationId: number,
  ): Promise<PrayerRow[]> => {
    const allRows: PrayerRow[] = [];

    // 2) Hent for alle 12 måneder
    for (let month = 1; month <= 12; month++) {
      setStatus(`Henter data for ${cityName}, måned ${month} av 12 …`);

      // Bønnetider - bruk backend proxy endpoint
      const prayerJson = await fetchPrayerTimes(
        method === "mawaqeet-external"
          ? `/api/v1/prayer-times/generate`
          : `/api/v1/prayer-times/generate-local`,
        "GET",
        undefined,
        {
          lat: String(lat),
          lon: String(lon),
          y: String(selectedYear),
          m: String(month),
          rounding: "1",
          is_isha_15: "1",
          ty: "m",
          method: isIrn ? "irn" : "mawaqeet",
        },
      );
      if (!prayerJson) {
        throw new Error(
          lastPrayerError()?.message ||
            `Kunne ikke hente bønnetider for måned ${month}.`,
        );
      }
      const prayerDays = prayerJson.content || [];

      let hijriMap = await hijriFor(month);
      const missingHijri = prayerDays.some((day) => {
        const g = day.prayer.other.gregorian;
        return !hijriMap[
          `${g.y}-${String(g.m).padStart(2, "0")}-${String(g.d).padStart(2, "0")}`
        ];
      });
      if (missingHijri && (await fillHijriYear()))
        hijriMap = await hijriFor(month);

      // Bygg rader
      for (const day of prayerDays) {
        const g = day.prayer.other.gregorian;
        const t = day.prayer.times;

        const gY = g.y;
        const gM = g.m;
        const gD = g.d;

        const dateKey = `${gY.toString().padStart(4, "0")}-${gM
          .toString()
          .padStart(2, "0")}-${gD.toString().padStart(2, "0")}`;

        const hijriRaw = hijriMap[dateKey];
        if (!hijriRaw) {
          throw new Error(`Fant ikke hijri-dato for ${dateKey}.`);
        }

        const hijriDate = convertHijriFromApi(hijriRaw);
        const dateStr = formatDateDDMMYYYY(gY, gM, gD);

        const fajr = fmtTime(t.fajr);
        const fajr_endtime = fmtTime(t.sunrise);
        const duhr = fmtTime(t.dhuhr);
        const asr1 = fmtTime(t.asr1);
        const asr2 = fmtTime(t.asr2);
        const maghrib = fmtTime(t.maghrib);
        const isha = fmtTime(t.isha);

        const jsDate = new Date(gY, gM - 1, gD);
        const summer = isSummerTime(jsDate, selectedYear);
        const asr = t.asr ? fmtTime(t.asr) : summer ? asr1 : asr2;

        allRows.push({
          date: dateStr,
          hijriDate,
          fajr,
          fajr_endtime,
          duhr,
          asr,
          asr_1x_shadow: asr1,
          asr_2x_shadow: asr2,
          maghrib,
          isha,
          locationId,
          prayerTimeTable: targetTableId ?? (isIrn ? 1 : 2),
        });
      }
    }

    // sorter rader (safe)
    allRows.sort((a, b) => {
      const [ad, am, ay] = a.date.split("-").map(Number);
      const [bd, bm, by] = b.date.split("-").map(Number);
      const aDate = new Date(ay, am - 1, ad);
      const bDate = new Date(by, bm - 1, bd);
      return aDate.getTime() - bDate.getTime();
    });

    return allRows;
  };

  const handleGenerate = async () => {
    if (!canGenerate) return;

    setIsLoading(true);
    setStatus("");
    setError(null);
    setRows([]);

    try {
      // 1) Hent lat/lon basert på modus
      let lat: number;
      let lon: number;
      let cityName: string;

      if (useCustomLocation) {
        lat = parseFloat(customLat.trim());
        lon = parseFloat(customLon.trim());
        cityName = customCityName.trim();

        if (isNaN(lat) || isNaN(lon)) {
          throw new Error("Ugyldig lat/lon-verdier. Bruk desimaltall.");
        }
      } else {
        if (!selectedLocation?.lat || !selectedLocation?.lon) {
          throw new Error("Mangler lat/lon for valgt lokasjon.");
        }
        lat = selectedLocation.lat;
        lon = selectedLocation.lon;
        cityName = selectedLocation.name;
      }

      const allRows = await buildYear(
        lat,
        lon,
        cityName,
        useCustomLocation ? 0 : selectedLocationId || 0,
      );

      setRows(allRows);
      setStatus(
        `Ferdig! Genererte ${allRows.length} dager for ${cityName} ${selectedYear}.`,
      );
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Noe gikk galt. Prøv igjen.");
      setStatus("");
    } finally {
      setIsLoading(false);
    }
  };

  const targetTable = tables.find((t) => t.id === targetTableId) || null;
  const canSave =
    rows.length > 0 &&
    !useCustomLocation &&
    !!selectedLocationId &&
    !!targetTable;
  const citiesWithCoordinates = locations.filter((l) => l.lat && l.lon);

  const postDays = async (
    locationId: number,
    days: PrayerRow[],
  ): Promise<SaveResult> => {
    const result = await saveApi.execute(
      "/api/v1/admin/calendar/generated-prayers",
      "POST",
      {
        locationId,
        prayerTableId: targetTableId,
        year: selectedYear,
        days: days.map(
          ({ locationId: _l, prayerTimeTable: _t, ...day }) => day,
        ),
      },
    );
    if (!result)
      throw new Error(
        saveApi.lastError()?.message || "Kunne ikke lagre tidene.",
      );
    return result;
  };

  /** Lagrer de genererte tidene for valgt by. Dager som finnes fra før, overskrives. */
  const handleSave = async () => {
    if (!canSave || !selectedLocationId || !targetTable) return;
    const city = getCityName();
    const ok = await confirm({
      title: `Lagre ${city} ${selectedYear} i iBønn?`,
      message: (
        <span>
          {rows.length} dager lagres i <b>«{targetTable.name}»</b> for {city}.
          Dager som finnes fra før, overskrives.
        </span>
      ),
      confirmLabel: "Lagre",
    });
    if (!ok) return;
    setSaving(true);
    setError(null);
    try {
      const result = await postDays(selectedLocationId, rows);
      setStatus(result.message);
    } catch (err: any) {
      setError(err.message || "Kunne ikke lagre tidene.");
    } finally {
      setSaving(false);
    }
  };

  /** Genererer og lagrer valgt år for alle byer med koordinater. Fortsetter ved feil, og viser dem til slutt. */
  const handleSaveAll = async () => {
    if (!targetTable) return;
    const cities = citiesWithCoordinates;
    const ok = await confirm({
      title: `Generer og lagre ${selectedYear} for alle byer?`,
      message: (
        <span>
          {cities.length} byer beregnes og lagres i <b>«{targetTable.name}»</b>.
          Dager som finnes fra før, overskrives. Det tar noen minutter; du kan
          stoppe underveis.
        </span>
      ),
      confirmLabel: "Generer og lagre",
    });
    if (!ok) return;
    stopAll.current = false;
    setSaving(true);
    setIsLoading(true);
    setError(null);
    let days = 0;
    let updated = 0;
    const failed: string[] = [];
    try {
      for (let i = 0; i < cities.length; i++) {
        const city = cities[i];
        if (stopAll.current) {
          setStatus(
            `Stoppet etter ${i} av ${cities.length} byer. ${days} dager lagret.`,
          );
          return;
        }
        setStatus(
          `${city.name} (${i + 1}/${cities.length}) … ${days} dager lagret.`,
        );
        try {
          const cityRows = await buildYear(
            city.lat,
            city.lon,
            city.name,
            city.id,
          );
          const result = await postDays(city.id, cityRows);
          days += result.saved;
          updated += result.updated;
        } catch (err: any) {
          failed.push(`${city.name}: ${err.message || "feilet"}`);
        }
      }
      setStatus(
        `Ferdig: ${days} dager lagret for ${cities.length - failed.length} byer i ${selectedYear} (${updated} overskrevet, ${days - updated} nye).`,
      );
      if (failed.length)
        setError(`${failed.length} byer feilet. ${failed.join(" · ")}`);
    } finally {
      setSaving(false);
      setIsLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    if (!rows.length) return;
    const cityName = getCityName();
    const locationId = getLocationId();

    // CSV har alltid alle kolonner uavhengig av filter
    const header: string[] = [
      "date",
      "hijriDate",
      "fajr",
      "fajr_endtime",
      "duhr",
      "asr",
      "asr_1x_shadow",
      "asr_2x_shadow",
      "maghrib",
      "isha",
      "locationId",
      "prayerTimeTable",
    ];

    const lines = [
      header.join(","),
      ...rows.map((r) => {
        const row: (string | number)[] = [
          r.date,
          r.hijriDate,
          r.fajr,
          r.fajr_endtime,
          r.duhr,
          r.asr,
          r.asr_1x_shadow,
          r.asr_2x_shadow,
          r.maghrib,
          r.isha,
          r.locationId,
          r.prayerTimeTable,
        ];
        return row.join(",");
      }),
    ];

    const csvContent = lines.join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${locationId} - ${cityName} ${selectedYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadExcel = async () => {
    if (!rows.length) return;
    const cityName = getCityName();
    const asrFilter: AsrFilterOptions = { onlyShafi, onlyHanafi };
    await generateStyledExcel(rows, cityName, selectedYear, asrFilter);
  };

  const handleDownloadPDF = async () => {
    if (!rows.length) return;
    const cityName = getCityName();
    const asrFilter: AsrFilterOptions = { onlyShafi, onlyHanafi };
    await generatePDF(rows, cityName, selectedYear, asrFilter);
  };

  // Gruppér til visning (valgfritt, rent UI)
  const rowsByMonth: Record<string, PrayerRow[]> = {};
  for (const r of rows) {
    const [dd, mm, yyyy] = r.date.split("-");
    const key = `${mm}-${yyyy}`;
    if (!rowsByMonth[key]) rowsByMonth[key] = [];
    rowsByMonth[key].push(r);
  }

  Object.keys(rowsByMonth).forEach((k) => {
    rowsByMonth[k].sort((a, b) => {
      const [ad] = a.date.split("-");
      const [bd] = b.date.split("-");
      return Number(ad) - Number(bd);
    });
  });

  const locationOptions = locations.map((location) => ({
    value: location.id,
    label: location.name,
  }));

  const customFilter = createFilter({
    ignoreCase: true,
    ignoreAccents: true,
    matchFrom: "any",
    stringify: (option) => option.label,
  });

  const monthKeys = Object.keys(rowsByMonth).sort((a, b) => {
    const [ma, ya] = a.split("-");
    const [mb, yb] = b.split("-");
    if (ya === yb) return Number(ma) - Number(mb);
    return Number(ya) - Number(yb);
  });
  const availableMonths = monthKeys.map((key) => Number(key.split("-")[0]));
  const previewKey =
    monthKeys.find((key) => Number(key.split("-")[0]) === previewMonth) ??
    monthKeys[0];
  const previewRows = previewKey ? rowsByMonth[previewKey] : [];
  const [previewMm, previewYyyy] = previewKey
    ? previewKey.split("-").map(Number)
    : [previewMonth, selectedYear];

  const previewColumns: Array<Column<PrayerRow>> = [
    {
      key: "date",
      header: "Dato",
      numeric: true,
      render: (r) => {
        const [dd, mm] = r.date.split("-");
        return (
          <b>
            {dd}.{mm}
          </b>
        );
      },
    },
    {
      key: "hijri",
      header: "Hijri",
      numeric: true,
      render: (r) => <span className={ui.help}>{r.hijriDate}</span>,
    },
    { key: "fajr", header: "Fajr", numeric: true, render: (r) => r.fajr },
    {
      key: "sunrise",
      header: "Soloppg.",
      numeric: true,
      render: (r) => r.fajr_endtime,
    },
    { key: "duhr", header: "Dhuhr", numeric: true, render: (r) => r.duhr },
    ...(!onlyHanafi
      ? [
          {
            key: "asr1",
            header: "Asr 1x",
            numeric: true,
            render: (r: PrayerRow) => r.asr_1x_shadow,
          },
        ]
      : []),
    ...(!onlyShafi
      ? [
          {
            key: "asr2",
            header: "Asr 2x",
            numeric: true,
            render: (r: PrayerRow) => r.asr_2x_shadow,
          },
        ]
      : []),
    {
      key: "maghrib",
      header: "Maghrib",
      numeric: true,
      render: (r) => r.maghrib,
    },
    { key: "isha", header: "Isha", numeric: true, render: (r) => r.isha },
  ];

  const asrChoice = onlyShafi ? "shafi" : onlyHanafi ? "hanafi" : "both";
  const setAsrChoice = (choice: "both" | "shafi" | "hanafi") => {
    setOnlyShafi(choice === "shafi");
    setOnlyHanafi(choice === "hanafi");
  };

  return (
    <>
      <PageHeader
        title="Generer bønnetider"
        subtitle={
          superAdmin
            ? "Beregn et helt år med IRN- eller Mawaqeet-tider for én by eller alle byer, og lagre dem i iBønn eller last dem ned."
            : "Beregn et helt år med Mawaqeet-tider for en by eller et punkt på kartet, og last ned som CSV, Excel eller PDF."
        }
      />

      <div className={styles.layout}>
        <Card title="Innstillinger">
          <Toggle
            id="custom-location-switch"
            checked={useCustomLocation}
            onChange={(e) => setUseCustomLocation(e.target.checked)}
            label={
              <span className={styles.toggleText}>
                <span className={styles.toggleTitle}>
                  Egendefinert lokasjon
                </span>
                <span className={ui.help}>
                  Skriv inn lat/lon og bynavn i stedet for å velge by
                </span>
              </span>
            }
          />

          {useCustomLocation ? (
            <>
              <div className={ui.fld}>
                <span className={ui.lbl}>Søk etter by (Google Places)</span>
                <PlacesAutocomplete
                  onPlaceSelected={handlePlaceSelected}
                  placeholder="Skriv inn bynavn …"
                />
              </div>
              <TextField
                id="customCityName"
                label="Bynavn (brukes i filnavn og PDF)"
                value={customCityName}
                onChange={(e) => setCustomCityName(e.target.value)}
                placeholder="F.eks. Oslo"
              />
              <div className={ui.g2}>
                <TextField
                  id="customLat"
                  label="Breddegrad (lat)"
                  value={customLat}
                  onChange={(e) => handleLatChange(e.target.value)}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    const pair = parseLatLonPair(pasted);
                    if (pair) {
                      e.preventDefault();
                      setCustomLat(pair.lat);
                      setCustomLon(pair.lon);
                    }
                  }}
                  placeholder="59.9139"
                  help="Lim inn «lat,lon» for å fylle begge."
                />
                <TextField
                  id="customLon"
                  label="Lengdegrad (lon)"
                  value={customLon}
                  onChange={(e) => setCustomLon(e.target.value)}
                  placeholder="10.7522"
                />
              </div>
              <span>
                <Button
                  variant={coordsCopied ? "mint" : "secondary"}
                  size="sm"
                  onClick={handleCopyLatLon}
                  disabled={!customLat.trim() || !customLon.trim()}
                  title="Kopier lat,lon"
                  icon={
                    <Icon
                      name={coordsCopied ? "check" : "copy"}
                      size={16}
                      strokeWidth={2}
                    />
                  }
                >
                  {coordsCopied ? "Kopiert" : "Kopier lat,lon"}
                </Button>
              </span>

              <CoordinateMap
                lat={parsedCustomLat}
                lon={parsedCustomLon}
                onCoordinateChange={handleMapCoordinateChange}
              />
            </>
          ) : (
            <div className={ui.fld}>
              <label htmlFor="locationSelect" className={ui.lbl}>
                By
              </label>
              <Select
                inputId="locationSelect"
                options={locationOptions}
                onChange={(selectedOption) =>
                  setSelectedLocationId(selectedOption?.value || null)
                }
                value={
                  selectedLocationId
                    ? locationOptions.find(
                        (opt) => opt.value === selectedLocationId,
                      )
                    : null
                }
                placeholder="Velg by"
                filterOption={customFilter}
                noOptionsMessage={() => "Ingen lokasjoner funnet"}
                isClearable={false}
                styles={adminSelectStyles<{ value: number; label: string }>()}
                menuPortalTarget={document.body}
              />
            </div>
          )}

          <div className={ui.fld} role="radiogroup" aria-label="Metode">
            <span className={ui.lbl}>Metode</span>
            <div className={styles.methods}>
              {(superAdmin
                ? (["irn", "mawaqeet", "mawaqeet-external"] as GenerateMethod[])
                : (["mawaqeet", "mawaqeet-external"] as GenerateMethod[])
              ).map((m) => (
                <Radio
                  key={m}
                  name="method"
                  label={METHOD_INFO[m].label}
                  checked={method === m}
                  disabled={isLoading || saving}
                  onChange={() => setMethod(m)}
                />
              ))}
            </div>
            <div className={styles.methodInfo}>
              <b>{METHOD_INFO[method].title}</b>
              <div className={ui.help}>{METHOD_INFO[method].text}</div>
            </div>
          </div>

          <div className={ui.fld} role="radiogroup" aria-label="Asr i eksport">
            <span className={ui.lbl}>Asr i eksport</span>
            <div className={styles.radios}>
              <Radio
                name="asrChoice"
                label="Begge"
                checked={asrChoice === "both"}
                onChange={() => setAsrChoice("both")}
              />
              <Radio
                id="only-shafi-checkbox"
                name="asrChoice"
                label="Kun Shafi'i (1x skygge)"
                checked={asrChoice === "shafi"}
                onChange={() => setAsrChoice("shafi")}
              />
              <Radio
                id="only-hanafi-checkbox"
                name="asrChoice"
                label="Kun Hanafi (2x skygge)"
                checked={asrChoice === "hanafi"}
                onChange={() => setAsrChoice("hanafi")}
              />
            </div>
          </div>

          <AdminSelect
            id="yearSelect"
            label="År"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {Array.from({ length: 10 }, (_, index) => {
              const year = 2025 + index;
              return (
                <option key={year} value={year}>
                  {year}
                </option>
              );
            })}
          </AdminSelect>

          <Button
            block
            onClick={handleGenerate}
            disabled={isLoading || !canGenerate}
            loading={isLoading}
            icon={<Icon name="wand" size={16} strokeWidth={2} />}
          >
            {isLoading ? "Genererer …" : "Generer tider"}
          </Button>
        </Card>

        <div className={styles.side}>
          {status && (
            <Alert tone={!isLoading && rows.length > 0 ? "ok" : "info"}>
              {status}
            </Alert>
          )}
          {error && <Alert tone="error">{error}</Alert>}

          <Card title="Last ned">
            <div className={ui.g3}>
              <Button
                variant="secondary"
                onClick={handleDownloadCSV}
                disabled={rows.length === 0}
                icon={<Icon name="doc" size={16} strokeWidth={2} />}
              >
                CSV
              </Button>
              <Button
                variant="secondary"
                onClick={handleDownloadExcel}
                disabled={rows.length === 0 || isIrn}
                icon={<Icon name="table" size={16} strokeWidth={2} />}
              >
                Excel
              </Button>
              <Button
                variant="secondary"
                onClick={handleDownloadPDF}
                disabled={rows.length === 0 || isIrn}
                icon={<Icon name="doc" size={16} strokeWidth={2} />}
              >
                PDF
              </Button>
            </div>
            <span className={ui.help}>
              CSV har alle 12 kolonner og kan importeres under Bønnetider. Excel
              og PDF har én side per måned med sommertid, ekstremperiode og
              forklaringer
              {isIrn
                ? " (bare for Mawaqeet, siden de har Mawaqeet-oppsett)"
                : ""}
              .
            </span>
          </Card>

          <Card title="Lagre i iBønn">
            <AdminSelect
              id="targetTable"
              label="Bønnetidstabell"
              value={targetTableId ?? ""}
              disabled={saving}
              onChange={(e) => setTargetTableId(Number(e.target.value) || null)}
            >
              {tables.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </AdminSelect>
            <div className={ui.g2}>
              <Button
                onClick={handleSave}
                disabled={saving || isLoading || !canSave}
                icon={<Icon name="check" size={16} strokeWidth={2} />}
              >
                {useCustomLocation
                  ? "Velg en by for å lagre"
                  : `Lagre ${getCityName()} ${selectedYear}`}
              </Button>
              {saving && isLoading ? (
                <Button
                  variant="secondary"
                  onClick={() => (stopAll.current = true)}
                >
                  Stopp etter denne byen
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  onClick={handleSaveAll}
                  disabled={
                    saving ||
                    isLoading ||
                    !targetTable ||
                    citiesWithCoordinates.length === 0
                  }
                  icon={<Icon name="wand" size={16} strokeWidth={2} />}
                >
                  Generer og lagre alle byer ({citiesWithCoordinates.length})
                </Button>
              )}
            </div>
            <span className={ui.help}>
              Lagrer tidene i iBønn-databasen for byen og valgt tabell, med
              Hijri-dato fra Hijri-kalenderen. Dager som finnes fra før for byen
              og datoen, overskrives – det blir aldri doble bønnetider.
              Egendefinerte lokasjoner kan bare lastes ned.
              {isIrn &&
                " IRN-tider kan ikke regnes ut for Nord-Norge med midnattssol og mørketid ennå; de byene hoppes over og listes til slutt."}
            </span>
          </Card>

          <Card title="Forhåndsvisning">
            {rows.length > 0 ? (
              <>
                <SegmentedControl
                  label="Måned"
                  className={styles.months}
                  value={previewMm}
                  onChange={setPreviewMonth}
                  options={availableMonths.map((m) => ({
                    value: m,
                    label: MONTH_SHORT_NO[m - 1],
                  }))}
                />
                <b className={styles.previewTitle}>
                  {getCityName()} – {MONTH_NAMES_NO[previewMm]} {previewYyyy}
                </b>
                <Table
                  compact
                  columns={previewColumns}
                  rows={previewRows}
                  rowKey={(r, idx) => `${r.date}-${idx}`}
                  caption="Forhåndsvisning av bønnetider"
                />
              </>
            ) : (
              <EmptyState icon="wand" title="Ingen tider generert ennå">
                Velg by eller lokasjon og år, og trykk «Generer tider».
              </EmptyState>
            )}
          </Card>
        </div>
      </div>
    </>
  );
};

export default MawaqeetGenerateTimes;
